import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Check, Calendar, MapPin, Tag, Loader2, AlertCircle, 
  X, MessageCircle, Copy, ShieldCheck, HeartPulse, User, Users, Church, PhoneCall, ArrowLeft, Info
} from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../mock';

const Registration = () => {
  const { registration } = mockData;
  const navigate = useNavigate();
  const location = useLocation();
  
  // --- ESTADOS DE CONTROLE ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // NOVO ESTADO: Controla se vemos o PIX ('pix') ou o aviso de pagar depois ('later')
  const [viewStep, setViewStep] = useState('pix'); 

  // --- ESTADOS PARA MÁSCARAS E LÓGICA ---
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [idade, setIdade] = useState("");

  // --- CONFIGURAÇÕES DE PAGAMENTO ---
  const chavePix = "06269004179";
  const seuNumero = "5562991653451"; 
  const mensagemWhatsapp = encodeURIComponent("Olá! Acabei de preencher minha inscrição para o Retiro Unificados 2026. Aqui está o meu comprovante de pagamento.");
  const linkWhatsapp = `https://wa.me/${seuNumero}?text=${mensagemWhatsapp}`;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('inscricao') === 'sucesso') {
      setShowPaymentModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  // --- FUNÇÕES DE MÁSCARA ---
  const handleCpfChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    value = value.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    setCpf(value.substring(0, 14));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    value = value.replace(/^(\d{2})(\d)/g, "($1) $2").replace(/(\d)(\d{4})$/, "$1-$2");
    setPhone(value.substring(0, 15));
  };

  const handleCopyPix = () => {
    navigator.clipboard.writeText(chavePix);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('formulario-inscricao');
    if (formElement) formElement.scrollIntoView({ behavior: 'smooth' });
  };

  // --- ENVIO DO FORMULÁRIO ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação de idade dinâmica baseada no mockData
    const idadeNum = parseInt(idade);
    if (idadeNum < 12 || idadeNum > 24) {
      setError(`A idade permitida para participação é de ${registration.details.ageRange}.`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch("https://formspree.io/f/mgopygld", {
        method: "POST",
        body: formData,
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        form.reset();
        setCpf("");
        setPhone("");
        setIdade("");
        setViewStep('pix'); 
        setShowPaymentModal(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const data = await response.json();
        setError(data.errors ? data.errors[0].message : "Erro ao processar inscrição.");
      }
    } catch (err) {
      setError("Falha na conexão. Verifique sua internet.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="inscricao" className="py-20 bg-[#F3F4F6] relative">
      
      {/* --- MODAL DE PAGAMENTO --- */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-[#071227]/95 flex items-center justify-center z-[100] p-4 backdrop-blur-md">
          <div className="bg-white rounded-[2.5rem] p-8 md:p-12 max-w-md w-full text-center border-t-[10px] border-orange-500 shadow-2xl relative animate-in fade-in zoom-in duration-300">
            
            <button onClick={() => setShowPaymentModal(false)} className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-all">
              <X size={28} />
            </button>

            {viewStep === 'pix' ? (
              <>
                <div className="mb-6 flex justify-center">
                  <div className="bg-green-100 p-4 rounded-full animate-bounce">
                    <Check className="text-green-600" size={40} strokeWidth={3} />
                  </div>
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Inscrição Enviada! 🎉</h2>
                <p className="text-gray-600 mb-8 leading-relaxed text-sm">Sua ficha foi recebida. Realize o pagamento via PIX para garantir sua vaga:</p>
                
                <div onClick={handleCopyPix} className="bg-orange-50 p-6 rounded-3xl border-2 border-dashed border-orange-200 mb-6 cursor-pointer hover:bg-orange-100 transition-all relative group">
                  <p className="text-[10px] text-orange-600 font-black uppercase tracking-widest mb-2">{copied ? "Copiado!" : "Clique para copiar o PIX"}</p>
                  <p className="text-sm font-mono font-bold text-gray-800 break-all">{chavePix}</p>
                </div>

                <div className="flex flex-col gap-4">
                  <a href={linkWhatsapp} target="_blank" rel="noopener noreferrer" className="w-full py-5 bg-[#25D366] text-white font-extrabold rounded-2xl hover:bg-[#20ba5a] transition-all shadow-lg flex items-center justify-center gap-3 text-lg">
                    <MessageCircle size={24} /> Enviar Comprovante
                  </a>
                  <Button onClick={() => setViewStep('later')} variant="outline" className="w-full py-5 border-gray-100 text-gray-400 font-bold rounded-2xl">Pagar mais tarde</Button>
                </div>
              </>
            ) : (
              <div className="animate-in slide-in-from-right-4 duration-300">
                <div className="mb-6 flex justify-center">
                  <div className="bg-orange-100 p-4 rounded-full">
                    <Calendar className="text-orange-600" size={40} />
                  </div>
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-4 tracking-tight">Tudo certo, Russo!</h2>
                <p className="text-gray-600 mb-8 leading-relaxed text-sm italic">
                  Sua inscrição está salva no nosso sistema. Lembre-se que a vaga só é confirmada após o envio do comprovante.
                </p>
                
                <div className="flex flex-col gap-4">
                  <Button onClick={() => setViewStep('pix')} className="w-full py-5 bg-orange-500 text-white font-black rounded-2xl hover:bg-orange-600 flex items-center justify-center gap-2">
                    <ArrowLeft size={20} /> Voltar para o PIX
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowPaymentModal(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      navigate('/'); 
                    }} 
                    variant="ghost" 
                    className="text-gray-400 font-bold"
                  >
                    Fechar e voltar ao site
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-2 italic">Próxima Edição</h2>
          <p className="text-xl text-orange-600 font-black tracking-widest uppercase italic">{registration.subtitle}</p>
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden">
          <div className="bg-[#071227] p-8 md:p-12 text-center relative">
            <h3 className="text-3xl md:text-4xl font-black text-white mb-10 italic">Ficha de Inscrição</h3>
            <div className="grid md:grid-cols-3 gap-6 text-white mb-10">
              <div className="bg-[#111C33] p-6 rounded-2xl border border-white/10 flex flex-col items-center">
                <Calendar className="text-orange-500 mb-3" size={20} />
                <span className="text-[10px] opacity-50 uppercase font-bold mb-1">Data</span>
                <p className="font-bold">{registration.details.date}</p>
              </div>
              <div className="bg-[#111C33] p-6 rounded-2xl border border-white/10 flex flex-col items-center">
                <MapPin className="text-orange-500 mb-3" size={20} />
                <span className="text-[10px] opacity-50 uppercase font-bold mb-1">Local</span>
                <p className="font-bold">{registration.details.location}</p>
              </div>
              <div className="bg-[#111C33] p-6 rounded-2xl border border-white/10 flex flex-col items-center">
                <Tag className="text-orange-500 mb-3" size={20} />
                <span className="text-[10px] opacity-50 uppercase font-bold mb-1">Investimento</span>
                <p className="text-2xl font-black text-orange-500">{registration.details.price}</p>
              </div>
            </div>
            <Button onClick={scrollToForm} className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-xl font-bold">Preencher Formulário Abaixo</Button>
          </div>

          <form id="formulario-inscricao" onSubmit={handleSubmit} className="p-8 md:p-12 space-y-12 bg-white">
            
            {/* 1. DADOS PESSOAIS */}
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row md:items-end justify-between border-b pb-4 gap-2">
                <h4 className="text-xl font-black text-gray-900 flex items-center gap-3 italic">
                  <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-lg flex items-center justify-center text-sm not-italic">1</span>
                  Dados Pessoais
                </h4>
                {/* DADO DINÂMICO DO MOCKDATA AQUI */}
                <span className="flex items-center gap-1.5 text-xs font-bold text-orange-600 bg-orange-50 px-3 py-1.5 rounded-full border border-orange-100">
                  <Info size={14} /> Idade permitida: {registration.details.ageRange}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input name="nome" placeholder="Nome completo" className="input-field md:col-span-2" required />
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase font-bold text-gray-400 ml-2 mb-1">Nascimento</label>
                    <input type="date" name="data_nascimento" className="input-field font-bold text-gray-700" required />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase font-bold text-gray-400 ml-2 mb-1">Idade</label>
                    <input type="number" name="idade" placeholder="Ex: 18" value={idade} onChange={(e) => setIdade(e.target.value)} className="input-field" required min="12" max="24" />
                  </div>
                </div>
                <input name="sexo" placeholder="Sexo" className="input-field" required />
                <input name="cpf" value={cpf} onChange={handleCpfChange} placeholder="CPF: 000.000.000-00" className="input-field" required />
                <input name="rg" placeholder="RG" className="input-field" required />
                <input name="profissao" placeholder="Profissão" className="input-field" />
                <input name="whatsapp" value={phone} onChange={handlePhoneChange} placeholder="WhatsApp" className="input-field" required />
                <input type="email" name="email" placeholder="E-mail" className="input-field" required />
                <select name="camiseta" className="input-field bg-white" required>
                  <option value="">Tamanho da Camiseta</option>
                  <option>PP</option><option>P</option><option>M</option><option>G</option><option>GG</option>
                </select>
                <input name="endereco" placeholder="Endereço completo" className="input-field md:col-span-2" required />
                <input name="cidade_estado" placeholder="Cidade / Estado" className="input-field md:col-span-2" required />
              </div>
            </div>

            {/* 2. RESPONSÁVEL (Aparece se for menor de 18) */}
            {idade && parseInt(idade) < 18 && (
              <div className="space-y-8 p-8 bg-orange-50 rounded-[2rem] border border-orange-100 animate-in fade-in slide-in-from-top-4 duration-500">
                <h4 className="text-xl font-black text-orange-800 flex items-center gap-3 italic">
                  <span className="bg-orange-200 text-orange-700 w-8 h-8 rounded-lg flex items-center justify-center text-sm not-italic">2</span>
                  Dados do Responsável (Menores de idade)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input name="nome_responsavel" placeholder="Nome completo do responsável" className="input-field bg-white" required />
                  <input name="parentesco" placeholder="Grau de parentesco" className="input-field bg-white" required />
                  <input name="cpf_responsavel" placeholder="CPF do responsável" className="input-field bg-white" required />
                  <input name="whatsapp_responsavel" placeholder="WhatsApp do responsável" className="input-field bg-white" required />
                  <input name="email_responsavel" placeholder="E-mail do responsável" className="input-field bg-white" />
                  <input name="endereco_responsavel" placeholder="Endereço (se diferente do participante)" className="input-field bg-white md:col-span-2" />
                </div>
              </div>
            )}

            {/* 3. IGREJA */}
            <div className="space-y-8">
              <h4 className="text-xl font-black text-gray-900 flex items-center gap-3 border-b pb-4 italic">
                <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-lg flex items-center justify-center text-sm not-italic">3</span>
                Informações de igreja
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input name="igreja" placeholder="Qual igreja você frequenta?" className="input-field md:col-span-2" />
                <input name="pastor" placeholder="Nome do pastor/líder" className="input-field" />
                <input name="tempo_congrega" placeholder="Há quanto tempo congrega?" className="input-field" />
                <div className="md:col-span-2 p-5 border border-gray-200 rounded-2xl flex items-center gap-6 bg-[#FCFDFF]">
                  <span className="text-sm font-bold text-gray-500">Participa de algum ministério?</span>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm font-bold"><input type="radio" name="tem_ministerio" value="Sim" /> Sim</label>
                    <label className="flex items-center gap-2 text-sm font-bold"><input type="radio" name="tem_ministerio" value="Não" /> Não</label>
                  </div>
                </div>
                <input name="qual_ministerio" placeholder="Se sim, qual?" className="input-field md:col-span-2" />
              </div>
            </div>

            {/* 4. SAÚDE */}
            <div className="space-y-8">
              <h4 className="text-xl font-black text-gray-900 flex items-center gap-3 border-b pb-4 italic">
                <span className="bg-red-100 text-red-600 w-8 h-8 rounded-lg flex items-center justify-center text-sm not-italic">4</span>
                Informações de Saúde
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <p className="text-sm font-bold text-gray-600 ml-1">🥗 Restrição Alimentar</p>
                  <textarea name="restricao_alimentar" placeholder="Possui alguma? Se sim, qual?" className="input-field h-24 pt-4 resize-none"></textarea>
                </div>
                <div className="space-y-3">
                  <p className="text-sm font-bold text-gray-600 ml-1">💊 Uso de Medicação</p>
                  <textarea name="medicacao" placeholder="Faz uso contínuo? Qual e horários?" className="input-field h-24 pt-4 resize-none"></textarea>
                </div>
                <div className="space-y-3">
                  <p className="text-sm font-bold text-gray-600 ml-1">🩺 Alergias / Doenças</p>
                  <textarea name="alergias_doencas" placeholder="Possui alergias ou condições (asma, diabetes...)? Detalhe aqui." className="input-field h-24 pt-4 resize-none"></textarea>
                </div>
                <div className="space-y-3">
                  <p className="text-sm font-bold text-gray-600 ml-1">🏃‍♂️ Limitação Física</p>
                  <textarea name="restricao_esporte" placeholder="Possui restrição para atividades físicas?" className="input-field h-24 pt-4 resize-none"></textarea>
                </div>
              </div>
            </div>

            {/* 5. EMERGÊNCIA */}
            <div className="space-y-8">
              <h4 className="text-xl font-black text-gray-900 flex items-center gap-3 border-b pb-4 italic">
                <span className="bg-gray-100 text-gray-600 w-8 h-8 rounded-lg flex items-center justify-center text-sm not-italic">5</span>
                Contato de Emergência
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <input name="emergencia_nome" placeholder="Nome do contato" className="input-field" required />
                <input name="emergencia_parentesco" placeholder="Grau de parentesco" className="input-field" required />
                <input name="emergencia_telefone" placeholder="Telefone de emergência" className="input-field" required />
              </div>
            </div>

            {/* TERMOS */}
            <div className="bg-orange-50/50 p-8 rounded-[2rem] border border-orange-100 space-y-5">
              <label className="flex items-start gap-4 cursor-pointer group">
                <input type="checkbox" name="uso_imagem" className="mt-1.5 w-5 h-5 accent-orange-500 rounded" required />
                <span className="text-sm text-gray-700 leading-relaxed font-medium">Autorizo o uso de minha imagem (fotos e vídeos) para divulgação da igreja.</span>
              </label>
              <label className="flex items-start gap-4 cursor-pointer group">
                <input type="checkbox" name="termo_responsabilidade" className="mt-1.5 w-5 h-5 accent-orange-500 rounded" required />
                <span className="text-sm text-gray-900 leading-relaxed font-black italic underline">Declaro que as informações acima são verdadeiras e estou ciente das normas do retiro.</span>
              </label>
            </div>

            {error && <div className="p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-2 font-bold"><AlertCircle size={20}/> {error}</div>}

            <div className="text-center pt-8">
              <Button type="submit" disabled={isSubmitting} className="bg-gradient-to-r from-orange-500 to-rose-500 text-white font-black text-xl px-16 py-8 rounded-2xl shadow-xl hover:scale-105 transition-all w-full md:w-auto">
                {isSubmitting ? <Loader2 className="animate-spin" /> : 'FINALIZAR MINHA INSCRIÇÃO'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .input-field {
          width: 100%;
          padding: 1.25rem;
          border: 1px solid #E5E7EB;
          border-radius: 1.25rem;
          background-color: #FCFDFF;
          outline: none;
          transition: all 0.2s;
          font-size: 0.875rem;
        }
        .input-field:focus {
          border-color: #F97316;
          box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.1);
        }
      `}</style>
    </section>
  );
};

export default Registration;